import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getCurrentUser } from './store/slices/authSlice';

// Компоненты
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import MainPage from './pages/MainPage';
import RouteList from './pages/Routes/RouteList';
import RouteDetail from './pages/Routes/RouteDetail';
import CreateRoute from './pages/Routes/CreateRoute';
import Profile from './pages/Profile/Profile';
import Collaboration from './pages/Collaboration/Collaboration';
import Debug from './pages/Debug';

// Стили
import './index.css';

// Защищенный маршрут
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth);
  
  // Отладочная информация
  console.log('ProtectedRoute render:', { isAuthenticated, loading });
  
  if (loading) {
    console.log('ProtectedRoute: showing loading...');
    return <div className="flex items-center justify-center min-h-screen">Загрузка...</div>;
  }
  
  if (!isAuthenticated) {
    console.log('ProtectedRoute: redirecting to login...');
    return <Navigate to="/login" />;
  }
  
  console.log('ProtectedRoute: rendering children...');
  return <>{children}</>;
};

// Компонент приложения
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading, user } = useAppSelector((state) => state.auth);

  // Отладочная информация
  console.log('AppContent render:', { isAuthenticated, loading, user });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    console.log('AppContent useEffect - token:', token, 'isAuthenticated:', isAuthenticated);
    if (token && !isAuthenticated) {
      console.log('Dispatching getCurrentUser...');
      dispatch(getCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <Router>
      <Routes>
        {/* Публичные маршруты */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        
        {/* Защищенные маршруты */}
        <Route path="/" element={
          <ProtectedRoute>
            <MainPage />
          </ProtectedRoute>
        } />
        
        {/* Дополнительные маршруты для совместимости */}
        <Route path="/old" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<RouteList />} />
          <Route path="routes" element={<RouteList />} />
          <Route path="routes/create" element={<CreateRoute />} />
          <Route path="routes/:id" element={<RouteDetail />} />
          <Route path="profile" element={<Profile />} />
          <Route path="collaboration" element={<Collaboration />} />
          <Route path="debug" element={<Debug />} />
        </Route>
        
        {/* Редирект на главную */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Главный компонент
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App; 